import { defineStore } from "pinia";
import { IDevice } from "../../models/Devices/device";
import { useDeviceStore } from "../device.store";
import { useParameterStore } from "../parameter.store";
import { DeviceTypes } from "../../constantes/constants";
import { IAnswerDeviceRequest } from "../../models/Devices/answerDeviceRequest";
import { getDateFrenchFormat } from "../../helper/dateFormatter";
import { useDevicePropertyStore } from "../deviceProperty.store";
import { IMonoDispatchLabelTemplate, IHeteroDispatchLabelTemplate } from "../../models/Devices/labels/dispatchLabelTemplate";
import { ILotLabelTemplate } from "../../models/Devices/labels/lotLabelTemplate";
import { IVolumeLabelTemplate } from "../../models/Devices/labels/volumeLabelTemplate";
import { IMonoSampleLabelTemplate, IHeteroSampleLabelTemplate } from "../../models/Devices/labels/sampleLabelTemplate";

import IDeviceProperty from "../../models/Devices/deviceProperty";
import { IHeteroDoseLabelTemplate, IMonoDoseLabelTemplate } from "../../models/Devices/labels/doseLabelTemplate";

export const useLabelStore = defineStore('label', {
    state: () => ({
        selectedPrinterId: 0 as number,
        selectedPrinterProperties: [] as IDeviceProperty[],

        availablePrinters: [] as IDevice[]
    }),
    getters: {
        hasActivePrinter(state) {
            return state.selectedPrinterId != 0;
        },

        selectedPrinter(state) {
            return state.availablePrinters.find(printer => printer.id == state.selectedPrinterId);
        }
    },
    actions: {
        init() {
            this.availablePrinters = [];

            const deviceStore = useDeviceStore();
            const paramStore = useParameterStore();

            let printerTypeId = paramStore.getParameterByCode3('DEVICE', 'TYPE', DeviceTypes.ZEBRA)?.id;
            this.availablePrinters = deviceStore.getDeviceByTypeForCurrentCenter(printerTypeId!);

            if(this.availablePrinters.length > 0) {
                this.selectedPrinterId = this.availablePrinters[0].id;

                const devicePropertyStore = useDevicePropertyStore();
                devicePropertyStore.currentDeviceId = this.selectedPrinterId;
                this.selectedPrinterProperties = devicePropertyStore.getCurrentDeviceProperties;
            }
        },

        async sendAnswerDeviceInstructions(instructions: string) {
            if(this.hasActivePrinter) {
                const deviceStore = useDeviceStore();
                let answerDeviceRequest: IAnswerDeviceRequest = {
                    instructions: instructions,
                    timeout: 0,
                };
        
                await deviceStore.sendMeasurementInstructions(this.selectedPrinterId, answerDeviceRequest);
            }
            else {
                console.error("No label printer device found to process the request");
            }
        },

        async printMonoDispatchLabel(t: IMonoDispatchLabelTemplate) {
            const prodDate = new Date(t.prodDate);
            const day = String(prodDate.getDate()).padStart(2, '0');
            const month = String(prodDate.getMonth() + 1).padStart(2, '0');
            const displayProdDate = `${day}/${month}`;

            let instr = `
                ^XA
                ~TA000
                ~JSN
                ^LT0t.
                ^MNW
                ^MTD
                ^PON
                ^PMN
                ^LH0,0
                ^JMA
                ^PR8,8
                ~SD15
                ^JUS
                ^LRN
                ^CI27
                ^PA0,1,1,0
                ^XZ
                ^XA
                ^MMT
                ^PW400
                ^LL400
                ^LS0
                ^FPH,2^FT337,98^A0N,17,18^FH\^CI28^FDDate^FS^CI27
                ^FPH,2^FT27,179^A0N,20,20^FH\^CI28^FDTotal:^FS^CI27
                ^FPH,2^FT217,179^A0N,20,20^FH\^CI28^FDDispo:^FS^CI27
                ^FO25,150^GB361,0,4^FS
                ^FO25,192^GB361,0,4^FS
                ^FPH,2^FT25,248^A1N,35,10^FH\^CI28^FDBlister:^FS^CI27
                ^FPH,2^FT25,349^A1N,35,10^FH\^CI28^FDSelfix:^FS^CI27
                ^FPH,2^FT204,248^A1N,35,10^FH\^CI28^FDVrac:^FS^CI27
                ^FPH,2^FT200,349^A1N,35,10^FH\^CI28^FDHtro:^FS^CI27
                ^FO14,75^GE112,67,4^FS
                ^FPH,8^FT5,57^A0N,50,40^FB395,1,10,C^FH\^CI28^FD${t.boarName}^FS^CI27
                ^SL0,3
                ^FPH,2^FT323,136^A0N,28,28
                ^FC%,{,#
                ^FH\^CI28^FD${displayProdDate}^FS^CI27
                ^BY2,3,37^FT141,120^BCN,,Y,N
                ^FH\^FD>:${t.boarNumber}^FS
                ^FT140,255^A0N,50,50^FH\^CI28^FD${t.postDispatchDoses > 0 ? t.blisterDoses : (t.blisterDoses > 0 ? t.blisterDoses : 0)}^FS^CI27
                ^FT140,350^A0N,50,50^FH\^CI28^FD${t.postDispatchDoses > 0 ?  t.selfixDoses : (t.selfixDoses > 0 ? t.selfixDoses : 0)}^FS^CI27
                ^FT280,255^A0N,50,50^FH\^CI28^FD${t.looseDoses * t.volume > 0 ? t.looseDoses * t.volume + t.primerVolume : 0}^FS^CI27
                ^FPH,2^FT359,255^A0N,20,20^FH\^CI28^FDml^FS^CI27
                ^FT280,350^A0N,50,50^FH\^CI28^FD${t.heteroDoses * t.volume > 0 ? t.heteroDoses * t.volume + t.primerVolume : 0}^FS^CI27
                ^FPH,2^FT359,350^A0N,20,20^FH\^CI28^FDml^FS^CI27
                ^FT60,105^A0N,30,25^FH\^CI28^FD${t.volume}^FS^CI27
                ^FT60,106^A0N,30,25^FH\^CI28^FD${t.volume}^FS^CI27
                ^FT60,107^A0N,30,25^FH\^CI28^FD${t.volume}^FS^CI27
                ^FT60,126^A0N,18,23^FH\^CI28^FDml^FS^CI27
                ^FT268,283^A0N,25,25^FH\^CI28^FD(Nb ${t.looseDoses})^FS^CI27               
                ^FT109,187^A0N,37,38^FH\^CI28^FD${t.preDisptachDoses}^FS^CI27
                ^FT295,187^A0N,37,38^FH\^CI28^FD${Math.max(0, t.postDispatchDoses)}^FS^CI27
                ^FT268,383^A0N,25,25^FH\^CI28^FD(Nb ${t.heteroDoses})^FS^CI27               
                ^LRY^FO3,10^GB389,0,59^FS
                ^LRN
                ^PQ1,0,1,Y
                ^XZ
            `

            await this.sendAnswerDeviceInstructions(instr);
        },

        async printHeteroDispatchLabel(t: IHeteroDispatchLabelTemplate) {
            const d = new Date(t.lotDate);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const displayDate = `${day}/${month}`;

            let instr = `
                ^XA
                ~TA000
                ~JSN
                ^LT0
                ^MNW
                ^MTD
                ^PON
                ^PMN
                ^LH0,0
                ^JMA
                ^PR8,8
                ~SD15
                ^JUS
                ^LRN
                ^CI27
                ^PA0,1,1,0
                ^XZ
                ^XA
                ^MMT
                ^PW400
                ^LL400
                ^LS0
                ^FPH,2^FT250,123^A0N,17,18^FH\^CI28^FDDate^FS^CI27
                ^FPH,2^FT27,179^A0N,20,20^FH\^CI28^FDTotal:^FS^CI27
                ^FPH,2^FT217,179^A0N,20,20^FH\^CI28^FDDispo:^FS^CI27
                ^FO25,150^GB361,0,4^FS
                ^FO25,192^GB361,0,4^FS
                ^FPH,2^FT25,248^A0N,20,20^FH\^CI28^FDSelfix:^FS^CI27
                ^FPH,2^FT205,255^A0N,20,20^FH\^CI28^FDBlister:^FS^CI27
                ^FPH,2^FT25,329^A0N,20,20^FH\^CI28^FDTube:^FS^CI27
                ^FPH,2^FT205,340^A0N,20,20^FH\^CI28^FDVrac:^FS^CI27
                ^FO100,104^GE77,40,3^FS
                ^SL0,3
                ^FPH,2^FT315,123^A0N,23,23
                ^FC%,{,#
                ^FH\^CI28^FD${displayDate}^FS^CI27
                ^BY3,3,49^FT95,98^BCN,,Y,Y
                ^FH\^FD>;${t.lotNumber}^FS
                ^FT127,255^A0N,37,38^FH\^CI28^FD${t.postDispatchDoses > 0 ? t.selfixDoses : (t.selfixDoses > 0 ? t.selfixDoses : 0)}^FS^CI27
                ^FT107,330^A0N,37,38^FH\^CI28^FD${t.postDispatchDoses > 0 ? t.tubeDoses : (t.tubeDoses > 0 ? t.tubeDoses : 0)}^FS^CI27
                ^FT307,254^A0N,37,38^FH\^CI28^FD${t.postDispatchDoses > 0 ? t.blisterDoses : (t.blisterDoses > 0 ? t.blisterDoses : 0)}^FS^CI27
                ^FT269,343^A0N,37,38^FH\^CI28^FD${t.looseDoses * t.volume > 0 ? t.looseDoses * t.volume + t.primerVolume : 0}^FS^CI27
                ^FPH,2^FT342,340^A0N,20,20^FH\^CI28^FDml^FS^CI27
                ^FT112,129^A0N,14,23^FH\^CI28^FD${t.volume} ml^FS^CI27
                ^FT269,380^A0N,25,25^FH\^CI28^FD(Nb ${t.looseDoses})^FS^CI27
                ^FT109,187^A0N,37,38^FH\^CI28^FD${t.preDisptachDoses}^FS^CI27
                ^FT295,187^A0N,37,38^FH\^CI28^FD${Math.max(0, t.postDispatchDoses)}^FS^CI27
                ^FT63,119^A0B,45,46^FH\^CI28^FD${t.schemaCode}^FS^CI27
                ^PQ1,0,1,Y
                ^XZ
            `

            await this.sendAnswerDeviceInstructions(instr);
        },

        async printHeteroLotLabel(t: ILotLabelTemplate) {
            let lotDate = new Date(t.lotDate);
            let day = String(lotDate.getDate()).padStart(2, '0');
            let month = String(lotDate.getMonth() + 1).padStart(2, '0');
            let displayProdDate = `${day}/${month}`;

            let instr = `
                ^XA
                ~TA000
                ~JSN
                ^LT0
                ^MNW
                ^MTD
                ^PON
                ^PMN
                ^LH0,0
                ^JMA
                ^PR8,8
                ~SD15
                ^JUS
                ^LRN
                ^CI27
                ^PA0,1,1,0
                ^XZ
                ^XA
                ^MMT
                ^PW400
                ^LL400
                ^LS0
                ^FPH,2^FT244,107^A0N,17,18^FH\^CI28^FDDate^FS^CI27
                ^FPH,2^FT36,151^A0N,20,20^FH\^CI28^FDTotal volume : ^FS^CI27
                ^FO22,118^GB361,0,4^FS
                ^FO27,170^GB361,0,4^FS
                ^FPH,2^FT291,196^APN,20,6^FH\^FDVol. ml^FS
                ^SL0,3
                ^FPH,2^FT301,105^A0N,23,23
                ^FC%,{,#
                ^FH\^CI28^FD${displayProdDate}^FS^CI27
                ^FPH,2^FT353,157^A0N,20,20^FH\^CI28^FDml^FS^CI27
                ^FT61,104^A0B,45,46^FH\^CI28^FD${t.schemaCode}^FS^CI27
                ^FPH,8^FT84,67^AUN,59,18^FH\^FD${t.lotNumber}^FS
                ^FPH,8^FT220,158^ATN,48,15^FH\^FD${t.boarVolumes.reduce((sum, el) => sum + el.respectiveVolume, 0)}^FS
                ${
                    t.boarVolumes.map((bv, index) => {
                        const netVolume = bv.respectiveVolume - (bv.primerVolume ?? 0);
                        const nbDoses = t.volumeDose && t.volumeDose > 0 ? Math.floor(netVolume / t.volumeDose) : null;
                        return `^FPH,2^FT29,${ 229 + 40 * index }^AQN,28,9^FH\^FD${bv.name.substring(0,17)}${nbDoses != null ? ` (${nbDoses})` : ''}^FS`
                    })
                }
                ${
                    t.boarVolumes.map((bv, index) => {
                        return `^FPH,8^FT285,${ 229 + 40 * index }^ARN,35,11^FH\^FD${bv.respectiveVolume}^FS`
                    })
                }
                ^LRY^FO72,21^GB327,0,61^FS
                ${
                    t.boarVolumes.map((bv, index) => {
                        return `^LRY^FO264,${ 200 + 40 * index }^GB117,0,34^FS`
                    })
                }
                ^LRN
                ^PQ1,0,1,Y
                ^XZ
            `

            await this.sendAnswerDeviceInstructions(instr);
        },

        async printVolumeLabel(t: IVolumeLabelTemplate) {   
            let prodDate = new Date();
            let day = String(prodDate.getDate()).padStart(2, '0');
            let month = String(prodDate.getMonth() + 1).padStart(2, '0');
            let displayProdDate = `${day}/${month}`;

            let instr = `
                ^XA
                ~TA000
                ~JSN
                ^LT0
                ^MNW
                ^MTD
                ^PON
                ^PMN
                ^LH0,0
                ^JMA
                ^PR8,8
                ~SD15
                ^JUS
                ^LRN
                ^CI27
                ^PA0,1,1,0
                ^XZ
                ^XA
                ^MMT
                ^PW400
                ^LL400
                ^LS0
                ^FO26,162^GB361,0,4^FS
                ^FT0,95^A0N,17,18^FB356,1,4,R^FH\^CI28^FDDate^FS^CI27
                ^FT31,219^AFN,26,13^FH\^FDVol. Ejac.:^FS
                ^FT31,276^AFN,26,13^FH\^FDVol Dil.Aj:^FS
                ^FT31,334^AFN,26,13^FH\^FDVol Final:^FS
                ^FT345,220^A0N,17,18^FH\^CI28^FDml^FS^CI27
                ^FT345,277^A0N,17,18^FH\^CI28^FDml^FS^CI27
                ^FT345,335^A0N,17,18^FH\^CI28^FDml^FS^CI27
                ^FPH,2^FT90,55^A0N,50,30^FB313,1,9,C^FH\^CI28^FD${t.boarName}^FS^CI27
                ^BY2,3,61^FT119,137^BCN,,Y,N
                ^FH\^FD>:${t.boarNumber}^FS
                ^FT64,126^A0B,52,53^FH\^CI28^FD${t.schemaCode}^FS^CI27
                ^SL0,3
                ^FT309,135^A0N,28,28
                ^FH\^CI28^FD${displayProdDate}^FS^CI27
                ^FT267,228^A0N,39,38^FH\^CI28^FD${t.ejacVolume}^FS^CI27                
                ^FT249,281^A0N,39,38^FH\^CI28^FD${t.totalVolume - t.ejacVolume}^FS^CI27 
                ^FT249,340^A0N,39,38^FH\^CI28^FD${t.totalVolume}^FS^CI27
                ^LRY^FO93,10^GB300,0,57^FS
                ^LRN
                ^PQ1,0,1,Y
                ^XZ
            `;

            await this.sendAnswerDeviceInstructions(instr);
        },

        async printMonoSampleLabel(t: IMonoSampleLabelTemplate) {
            let displayProdDate = getDateFrenchFormat(new Date(), false);

            let instr = `
                ^XA
                ~TA000
                ~JSN
                ^LT0
                ^MNW
                ^MTD
                ^PON
                ^PMN
                ^LH0,0
                ^JMA
                ^PR8,8
                ~SD15
                ^JUS
                ^LRN
                ^CI27
                ^PA0,1,1,0
                ^XZ
                ^XA
                ^MMT
                ^PW400
                ^LL400
                ^LS0
                ^FO26,165^GB361,0,4^FS
                ^FPH,1^FT22,212^ACN,18,10^FH\^FDDate de collecte:^FS
                ^FPH,2^FT87,57^A0N,50,30^FB313,1,9,C^FH\^CI28^FD${t.boarName}^FS^CI27
                ^BY2,3,61^FT159,136^BCN,,Y,N
                ^FH\^FD>:${t.boarNumber}^FS
                ^FT69,135^A0B,52,53^FH\^CI28^FD${t.schemaCode}^FS^CI27
                ^SL0,3
                ^FT253,214^A0N,31,30
                ^FH\^CI28^FD${displayProdDate}^FS^CI27
                ^LRY^FO93,10^GB300,0,57^FS
                ^LRN
                ^PQ${t.labelAmount ?? 1},0,1,Y
                ^XZ
            `;

            await this.sendAnswerDeviceInstructions(instr);
        },

        async printHeteroSampleLabel(t: IHeteroSampleLabelTemplate) {
            let displayProdDate = getDateFrenchFormat(new Date(), false);

            let instr = `
                ^XA
                ~TA000
                ~JSN
                ^LT0
                ^MNW
                ^MTD
                ^PON
                ^PMN
                ^LH0,0
                ^JMA
                ^PR8,8
                ~SD15
                ^JUS
                ^LRN
                ^CI27
                ^PA0,1,1,0
                ^XZ
                ^XA
                ^MMT
                ^PW400
                ^LL400
                ^LS0
                ^FO26,165^GB361,0,4^FS
                ^FPH,1^FT22,212^ACN,18,10^FH\^FDDate de collecte:^FS
                ^FPH,8^FT87,57^A0N,50,40^FB313,1,9,C^FH\^CI28^FD${t.lotNumber}^FS^CI27
                ^BY2,3,61^FT100,136^BCN,,Y,N
                ^FH\^FD>:${t.lotNumber}^FS
                ^FT69,135^A0B,52,53^FH\^CI28^FD${t.schemaCode}^FS^CI27
                ^SL0,3
                ^FT253,214^A0N,31,30
                ^FH\^CI28^FD${displayProdDate}^FS^CI27
                ^LRY^FO93,10^GB300,0,57^FS
                ^LRN
                ^PQ${t.labelAmount ?? 1},0,1,Y
                ^XZ
            `;

            await this.sendAnswerDeviceInstructions(instr);
        },

        async printMonoDoseLabel(t: IMonoDoseLabelTemplate) {
            let displayDate = getDateFrenchFormat(new Date(t.prodDate), true);

            let instr = `
                ^XA
                ~TA000
                ~JSN
                ^LT0
                ^MNW
                ^MTT
                ^PON
                ^PMN
                ^LH0,0
                ^JMA
                ^PR2,2
                ~SD15
                ^JUS
                ^LRN
                ^CI27
                ^PA0,1,1,0
                ^XZ
                ^XA
                ^MMT
                ^PW400
                ^LL400
                ^LS0
                ^FT0,46^A0N,20,20^FH\^CI28^FB400,1,0,C^FD${t.company}^FS^CI27
                ^FT30,85^A0N,15,15^FH\^CI28^FD${t.approvalCode}^FS^CI27
                ^FT150,85^A0N,15,15^FH\^CI28^FD${t.schemaCodeLabel}^FS^CI27
                ^FT340,85^A0N,15,15^FH\^CI28^FD${t.volume}^FS^CI27
                ^FPH,3
                ^FT358,85^A0N,15,15^FH\^CI28^FDml^FS^CI27
                ^FO25,90^GB350,40,2^FS
                ^FT0,125^A0N,35,35^FH\^CI28^FB400,1,0,C^FD${t.boarName}^FS^CI27
                ^FT30,150^A0N,15,15^FH\^CI28^FD${t.boarTattoo}^FS^CI27
                ^FT320,150^A0N,15,15^FH\^CI28^FD${t.nn}^FS^CI27
                ^FPH,1
                ^FT30,200^A0N,17,17^FH\^FDDate de collecte:^FS
                ^FT200,200^A0N,17,17^FH\^CI28^FD${displayDate}^FS^CI27
                ^FPH,1
                ^BY2,2,75
                ^FT20,330^BCN,,Y,N
                ^FH\^FD>:${t.boarNumber}^FS
                ^LRN
                ^PQ${t.labelAmount ?? 1},0,1,Y
                ^XZ
            `;

            await this.sendAnswerDeviceInstructions(instr);
        },

        async printHeteroDoseLabel(t: IHeteroDoseLabelTemplate) {
            let displayDate = getDateFrenchFormat(new Date(t.lotDate), true);        

            let instr = `
                ^XA
                ~TA000
                ~JSN
                ^LT0
                ^MNW
                ^MTT
                ^PON
                ^PMN
                ^LH0,0
                ^JMA
                ^PR2,2
                ~SD15
                ^JUS
                ^LRN
                ^CI27
                ^PA0,1,1,0
                ^XZ
                ^XA
                ^MMT
                ^PW400
                ^LL400
                ^LS0
                ^FT0,46^A0N,20,20^FH\^CI28^FB400,1,0,C^FD${t.company}^FS^CI27
                ^FT30,85^A0N,15,15^FH\^CI28^FD${t.approvalCode}^FS^CI27
                ^FT150,85^A0N,15,15^FH\^CI28^FD${t.schemaCodeLabel}^FS^CI27
                ^FT340,85^A0N,15,15^FH\^CI28^FD${t.volume}^FS^CI27
                ^FPH,3
                ^FT358,85^A0N,15,15^FH\^CI28^FDml^FS^CI27
                ^FO25,90^GB350,40,2^FS
                ^FT0,122^A0N,33,33^FH\^CI28^FB400,1,0,C^FD${t.schemaCode + " " + t.lotNumber}^FS^CI27
                ^FT30,150^A0N,18,18^FH\^FD${t.nn}^FS
                ^FT60,150^A0N,18,18^FH\^FD${ t.armada ? "@" : "" }^FS
                ^FT30,180^A0N,18,18^FH\^FD${t.boarTattoos[0] ?? ""}^FS
                ^FT30,210^A0N,18,18^FH\^FD${t.boarTattoos[1] ?? ""}^FS
                ^FT30,240^A0N,18,18^FH\^FD${t.boarTattoos[2] ?? ""}^FS
                ^FT210,180^A0N,18,18^FH\^FD${t.boarTattoos[3] ?? ""}^FS
                ^FT210,210^A0N,18,18^FH\^FD${t.boarTattoos[4] ?? ""}^FS
                ^FT30,290^A0N,17,17^FH\^FDDate de collecte:^FS
                ^FT190,290^A0N,17,17^FH\^CI28^FD${displayDate}^FS^CI27
                ^FT30,320^A0N,17,17^FH\^FDDate de peremption:^FS
                ^FT190,320^A0N,17,17^FH\^CI28^FD${displayDate}^FS^CI27
                ^BY2,2,40
                ^FT30,370^BCN,,Y,N
                ^FH\^FD${t.lotNumber}${t.volume}^FS
                ^LRN
                ^PQ${t.labelAmount ?? 1},0,1,Y
                ^XZ
            `;

            await this.sendAnswerDeviceInstructions(instr); 
        }
    }
})